import React from "react";
import localforage from "localforage";
import CircularProgress from "@mui/material/CircularProgress";
import { listEntries, type NegotiationBalanceEntry } from "../../services/negotiationBalance";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import { entryLabel, formatInstant } from "./negotiationBalanceFormat";
import "./NegotiationBalance.css";

export type BalanceHistoryViewProps = {
  entries: NegotiationBalanceEntry[];
  /** Present while Payments has an older page. */
  nextCursor?: string;
  loading?: boolean;
  /** Application-authored copy only. */
  error?: string;
  onLoadMore: (cursor: string) => void;
  onRetry: () => void;
};

function signedBRL(cents: number): string {
  return cents > 0 ? `+${formatBRL(cents)}` : `−${formatBRL(Math.abs(cents))}`;
}

/** The auction cycle a consumption paid for, by the short form of its id. */
function entryDetail(entry: NegotiationBalanceEntry): string | null {
  if (entry.kind !== "consumption_debit") return null;
  if (entry.cycleId) return `Ciclo ${entry.cycleId.slice(0, 8)}`;
  if (entry.invoiceId) return `Apuração ${entry.invoiceId.slice(0, 8)}`;
  return null;
}

/**
 * The Saldo de Negociações ledger, newest first: recharges (each credits
 * exactly the amount the company chose; the top-up fee it paid on top never
 * reaches the balance), consumption per auction cycle, refunds and reversals.
 * Amounts are exactly the signed entries Payments recorded.
 * @story layout padded
 * @story args { entries: [{ id: "e3", kind: "consumption_debit", amountCents: -12000, createdAt: "2026-10-28T21:05:00Z", invoiceId: "4c1d8a0e-2b7f-5b3a-9d1e-7a6f5e4d3c2b", cycleId: "6f0e1d2c-3b4a-4958-8776-655443322110" }, { id: "e1", kind: "topup_credit", amountCents: 100000, createdAt: "2026-10-28T14:10:00Z", topupId: "t1" }], nextCursor: "page-2" }
 * @story state Empty { entries: [], nextCursor: undefined }
 * @story state Loading { entries: [], nextCursor: undefined, loading: true }
 * @story state Failed { entries: [], nextCursor: undefined, error: "Não foi possível carregar o histórico do saldo." }
 * @story state Reversal { entries: [{ id: "e4", kind: "reversal_debit", amountCents: -5000, createdAt: "2026-10-29T10:00:00Z", topupId: "t2", reason: "pix_returned" }], nextCursor: undefined }
 */
export function BalanceHistoryView({
  entries,
  nextCursor,
  loading = false,
  error,
  onLoadMore,
  onRetry,
}: BalanceHistoryViewProps) {
  return (
    <div className="negotiationBalanceHistory" data-testid="negotiation-balance-history">
      {entries.length > 0 && (
        <ul className="negotiationBalanceHistoryList">
          {entries.map((entry) => {
            const detail = entryDetail(entry);
            return (
              <li
                className="negotiationBalanceHistoryItem"
                key={entry.id}
                data-testid="negotiation-balance-entry"
              >
                <span>
                  {entryLabel(entry)}
                  <small>
                    {formatInstant(entry.createdAt) ?? ""}
                    {detail ? ` · ${detail}` : ""}
                  </small>
                </span>
                <span
                  className={
                    entry.amountCents > 0
                      ? "negotiationBalanceHistoryCredit"
                      : "negotiationBalanceHistoryDebit"
                  }
                >
                  {signedBRL(entry.amountCents)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {!loading && !error && entries.length === 0 && (
        <p className="negotiationBalanceHint">Nenhuma movimentação ainda.</p>
      )}
      {error && (
        <p className="negotiationBalanceHint" role="alert">
          {error}{" "}
          <button type="button" className="negotiationRechargeSecondary" onClick={onRetry}>
            Tentar de novo
          </button>
        </p>
      )}
      {loading ? (
        <CircularProgress size={16} thickness={5} sx={{ color: "var(--brand-9)" }} />
      ) : (
        nextCursor &&
        !error && (
          <div>
            <button
              type="button"
              className="negotiationRechargeSecondary"
              onClick={() => onLoadMore(nextCursor)}
            >
              Carregar mais
            </button>
          </div>
        )
      )}
    </div>
  );
}

export type BalanceHistoryProps = {
  /** A change (a new balance revision) reloads the first page. */
  reloadKey?: string | number;
  pageSize?: number;
};

type HistoryState = {
  entries: NegotiationBalanceEntry[];
  nextCursor?: string;
  loading: boolean;
  error?: string;
};

const HISTORY_ERROR = "Não foi possível carregar o histórico do saldo.";

/**
 * Reads GET /payment/negotiation-balance/entries page by page (`nextCursor`)
 * and reloads the first page when the balance revision changes.
 * @story skip pagination lifecycle is covered by CI unit tests; see BalanceHistoryView for every visual state
 */
export function BalanceHistory({ reloadKey, pageSize = 20 }: BalanceHistoryProps) {
  const [state, setState] = React.useState<HistoryState>({ entries: [], loading: true });
  const sequenceRef = React.useRef(0);

  const load = React.useCallback(
    async (cursor?: string) => {
      const sequence = ++sequenceRef.current;
      setState((previous) => ({ ...previous, loading: true, error: undefined }));
      let jwt: string | null | undefined;
      try {
        jwt = await localforage.getItem<string>("JWT");
      } catch {
        jwt = undefined;
      }
      if (sequence !== sequenceRef.current) return;
      const result = jwt ? await listEntries(jwt, { cursor, limit: pageSize }) : undefined;
      if (sequence !== sequenceRef.current) return;
      if (!result?.ok) {
        setState((previous) => ({ ...previous, loading: false, error: HISTORY_ERROR }));
        return;
      }
      setState((previous) => ({
        entries: cursor ? [...previous.entries, ...result.value.entries] : result.value.entries,
        nextCursor: result.value.nextCursor,
        loading: false,
      }));
    },
    [pageSize],
  );

  React.useEffect(() => {
    void load();
    return () => {
      sequenceRef.current += 1;
    };
  }, [load, reloadKey]);

  return (
    <BalanceHistoryView
      entries={state.entries}
      nextCursor={state.nextCursor}
      loading={state.loading}
      error={state.error}
      onLoadMore={(cursor) => void load(cursor)}
      onRetry={() => void load(state.entries.length > 0 ? state.nextCursor : undefined)}
    />
  );
}
